import React, { useState, useEffect } from "react";
import { useManagementStore } from "../../../store/managementStore";
import {
	XMarkIcon,
	CheckIcon,
	PhoneIcon,
	EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

// 전화상담관리 상세보기 모달 컴포넌트
const CallDetailModal = ({ isOpen, onClose, itemData }) => {
	// managementStore에서 필요한 함수 가져오기
	const { handleFinishCall, handleReMessage } = useManagementStore();

	// 상태 관리
	const [teamList, setTeamList] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [actionLoading, setActionLoading] = useState(false);

	// 모달이 열릴 때 데이터 로드
	useEffect(() => {
		if (isOpen && itemData) {
			setTeamList(itemData);
		}
	}, [isOpen, itemData]);

	// 모달이 닫혀 있으면 렌더링하지 않음
	if (!isOpen) return null;

	// 모달 바깥 클릭 핸들러
	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	// 상담완료 처리 함수
	const handleFinishCallClick = async (item) => {
		try {
			setActionLoading(true);

			// 원본 코드에서 사용하는 형태로 데이터 구성
			const parseData = {
				user_id: item.user_id,
				RES_NM: item.RES_NM,
			};

			const result = await handleFinishCall(parseData);

			if (result.success) {
				toast.success("상담이 완료 처리되었습니다.");

				// 현재 시간 또는 API에서 반환된 시간 사용
				const finishedTime = new Date().toISOString();

				// 데이터 갱신 - 현재 아이템의 상태 즉시 업데이트
				const updatedItem = {
					...item,
					call_finished_flag: true,
					call_finished_time: finishedTime,
				};

				// 해당 아이템만 업데이트하여 UI에 즉시 반영
				if (teamList && Array.isArray(teamList)) {
					// teamList가 배열인 경우
					setTeamList(
						teamList.map((team) =>
							team.user_id === item.user_id ? updatedItem : team
						)
					);
				} else if (
					teamList &&
					teamList.team_list &&
					Array.isArray(teamList.team_list)
				) {
					// teamList가 team_list 속성을 가진 객체인 경우
					setTeamList({
						...teamList,
						team_list: teamList.team_list.map((team) =>
							team.user_id === item.user_id ? updatedItem : team
						),
					});
				}

				// 현재 렌더링 중인 아이템 직접 업데이트 (참조 업데이트)
				Object.assign(item, updatedItem);
			} else {
				throw new Error(result.error);
			}
		} catch (error) {
			console.error("상담완료 처리 중 오류 발생:", error);
			toast.error("알 수 없는 문제가 발생했습니다.");
		} finally {
			setActionLoading(false);
		}
	};

	// 문자 재발송 처리 함수
	const handleReMessageClick = async (item) => {
		// 확인 창 표시
		if (!window.confirm("문자를 재발송하시겠습니까?")) {
			return;
		}

		try {
			setActionLoading(true);

			// 원본 코드에서 사용하는 형태로 데이터 구성
			const parseData = {
				user_id: item.user_id,
			};

			const result = await handleReMessage(parseData);

			if (result.success) {
				toast.success("문자가 재발송되었습니다.");

				// // 데이터 갱신
				// setTeamList((prevList) => {
				// 	return prevList.team_list.map((team) => {
				// 		if (team.user_id === item.user_id) {
				// 			return {
				// 				...team,
				// 				// sms_status: "재발송",
				// 				// sms_sent_time:
				// 				// 	result.data?.sms_sent_time ||
				// 				// 	new Date().toISOString(),
				// 			};
				// 		}
				// 		return team;
				// 	});
				// });
			} else {
				throw new Error(result.error);
			}
		} catch (error) {
			console.error("문자 재발송 중 오류 발생:", error);
			toast.error("문자 재발송에 실패했습니다.");
		} finally {
			setActionLoading(false);
		}
	};

	// 티켓 링크 생성 함수
	const createTicketLink = (userId) => {
		const RES_DAY = userId.split(".")[0].split("-")[0];
		const RES_SEQ = userId.split(".")[0].split("-")[1];

		const reservationDate = RES_DAY;
		const reservationSeq = RES_SEQ;

		// 링크 생성
		const ticketUrl = `http://ktxtourticket.co.kr:8080/?reservationSeq=${reservationSeq}&reservationDate=${reservationDate}&userId=${userId}`;
		return (
			<a
				href={ticketUrl}
				target="_blank"
				rel="noreferrer"
				className="text-blue-600 hover:underline"
			>
				링크
			</a>
		);
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50"
			onClick={handleBackdropClick}
		>
			<div className="relative w-full max-w-7xl mx-auto my-6 max-h-[90vh] overflow-auto">
				<div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
					{/* 모달 헤더 */}
					<div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
						<h3 className="text-2xl font-semibold">
							전화상담 관리 상세
						</h3>
						<button
							className="p-1 ml-auto bg-transparent border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
							onClick={onClose}
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
					</div>

					{/* 행사 정보 섹션 */}
					<div className="bg-gray-50 rounded-lg p-4 shadow-sm">
						<h4 className="text-lg font-medium text-gray-900 mb-3">
							행사 정보
						</h4>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="bg-white p-3 rounded-md shadow-sm">
								<div className="grid grid-cols-3 gap-2">
									<div className="text-sm font-medium text-gray-500">
										행사명
									</div>
									<div className="col-span-2 text-sm text-gray-900">
										{itemData.ev_nm || "-"}
									</div>
								</div>
							</div>
							<div className="bg-white p-3 rounded-md shadow-sm">
								<div className="grid grid-cols-3 gap-2">
									<div className="text-sm font-medium text-gray-500">
										출발일
									</div>
									<div className="col-span-2 text-sm text-gray-900">
										{itemData.start_day
											? `${itemData.start_day.substring(
													0,
													4
											  )}/${itemData.start_day.substring(
													4,
													6
											  )}/${itemData.start_day.substring(
													6,
													8
											  )}`
											: "-"}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* 행사 정보 표시 영역 */}
					{itemData && itemData.length > 0 && itemData.event_info && (
						<div className="bg-gray-100 p-4 border-b border-gray-300">
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div className="flex items-center">
									<span className="font-semibold mr-2">
										행사번호:
									</span>
									<span>
										{itemData.event_info.event_id || "-"}
									</span>
								</div>
								<div className="flex items-center">
									<span className="font-semibold mr-2">
										출발일:
									</span>
									<span>
										{itemData.event_info.start_day || "-"}
									</span>
								</div>
								<div className="flex items-center">
									<span className="font-semibold mr-2">
										행사명:
									</span>
									<span>
										{itemData.event_info.event_name || "-"}
									</span>
								</div>
							</div>
						</div>
					)}

					{/* 모달 본문 */}
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						{loading ? (
							<div className="flex justify-center items-center py-10">
								<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
								<span className="ml-3 text-gray-600">
									로딩 중...
								</span>
							</div>
						) : error ? (
							<div className="text-center py-10">
								<p className="text-red-500">{error}</p>
							</div>
						) : (
							<div className="space-y-6">
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gradient-to-r from-blue-50 to-blue-100">
											<tr>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[5%]"
												>
													번호
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[15%]"
												>
													고객이름
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[15%]"
												>
													예약처
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]"
												>
													예약인원
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[15%]"
												>
													휴대전화 번호
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]"
												>
													문자응답
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[20%]"
												>
													전화확인관리
												</th>
												<th
													scope="col"
													className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]"
												>
													모바일 링크
												</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{itemData &&
												itemData.team_list.map(
													(item, index) => (
														<tr key={index}>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																{index + 1}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
																{item.RES_NM ||
																	"-"}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																{item.SITE_CD ||
																	"-"}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																{item.RES_CNT ||
																	"-"}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																{item.user_phone ||
																	"-"}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																{item.status ===
																"미응답" ? (
																	<span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-red-50 to-red-100 text-red-800 border border-red-200 shadow-sm">
																		<span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
																		미응답
																	</span>
																) : item.status ===
																  "전화 필요" || item.status === "필요 없음" ? (
																	<span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-green-50 to-green-100 text-green-800 border border-green-200 shadow-sm">
																		<span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
																		응답
																	</span>
																) : (
																	<span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 border border-gray-200 shadow-sm">
																		<span className="w-2 h-2 rounded-full bg-gray-500 mr-1.5"></span>
																		{item.status ||
																			"-"}
																	</span>
																)}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																<div className="flex space-x-2">
																	{/* 상담완료 버튼 - 상태에 따라 다르게 표시 */}
																	{(item.status ===
																		"미응답" &&
																		!item.call_finished_flag) ||
																	(item.status ===
																		"전화 필요" &&
																		!item.call_finished_flag) ? (
																		<button
																			onClick={() =>
																				handleFinishCallClick(
																					item
																				)
																			}
																			disabled={
																				actionLoading ||
																				!item.user_id
																			}
																			className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
																		>
																			<PhoneIcon className="h-4 w-4 mr-1.5" />
																			상담완료처리
																		</button>
																	) : item.status ===
																			"전화 필요" &&
																	  item.call_finished_flag ? (
																		<div className="flex items-center">
																			<button
																				disabled
																				className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-sm font-medium rounded-lg text-gray-500 bg-gray-100 shadow-sm cursor-not-allowed"
																			>
																				<CheckIcon className="h-4 w-4 mr-1.5" />
																				확인완료
																			</button>
																			<span className="ml-2 text-xs text-gray-500">
																				{item.call_finished_time
																					? `${
																							item.call_finished_time.split(
																								"T"
																							)[0]
																					  } ${item.call_finished_time
																							.split(
																								"T"
																							)[1]
																							.substring(
																								0,
																								8
																							)}`
																					: ""}
																			</span>
																		</div>
																	) : (
																		<button
																			disabled
																			className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-sm font-medium rounded-lg text-gray-400 bg-gray-50 shadow-sm cursor-not-allowed"
																		>
																			<CheckIcon className="h-4 w-4 mr-1.5" />
																			확인완료
																		</button>
																	)}

																	{/* 문자재발송 버튼 - 항상 표시되지만 조건에 따라 비활성화 */}
																	{((item.status ===
																		"미응답" &&
																		!item.call_finished_flag) ||
																		(item.status ===
																			"전화 필요" &&
																			!item.call_finished_flag)) && (
																		<button
																			onClick={() =>
																				handleReMessageClick(
																					item
																				)
																			}
																			disabled={
																				actionLoading ||
																				!item.user_id ||
																				!item.user_phone
																			}
																			className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
																		>
																			<EnvelopeIcon className="h-4 w-4 mr-1.5" />
																			문자재발송
																		</button>
																	)}
																</div>
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																{item.user_id
																	? createTicketLink(
																			item.user_id
																	  )
																	: "-"}
															</td>
														</tr>
													)
												)}
											{(!teamList ||
												teamList.length === 0) && (
												<tr>
													<td
														colSpan="8"
														className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500"
													>
														데이터가 없습니다.
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						)}
					</div>

					{/* 모달 푸터 */}
					<div className="flex items-center justify-end p-4 border-t border-solid border-gray-300 rounded-b bg-gray-50">
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							닫기
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CallDetailModal;
